import ValidationError from '@errors/ValidationError';
import PropertyErrorsInterface from '@form/validation/PropertyErrorsInterface';
import ItemDataSkeletton from '@js/jsonapi/skeletton/ItemDataSkeletton';
import CollectionSkeletton from '@js/jsonapi/skeletton/CollectionSkeletton';
import StoreData from '@form/form/StoreData';
import FormFieldMapInterface from '@form/form/FormFieldMapInterface';
import {Ref, ref} from '@vue/composition-api';
import ItemSkeletton from '@js/jsonapi/skeletton/ItemSkeletton';

interface StoreSharedInterface {
  hasPotentiallyRequiredFields: boolean;
  hasResult: boolean;
  isReadOnly: boolean
  labelTranslationKeyPrefix: string
  propertyErrors: PropertyErrorsInterface[];
  readOnlyProperties: string[];
}

export default class Form {
  private allowedEmptyProperties: Ref<string[]> = ref([]);

  private context = 'main';

  private data: StoreData = {};

  private readonly formFieldMap: FormFieldMapInterface = {};

  private initialData = '';

  private localData: StoreData = {};

  private propertyNamePrefix = '/data/attributes/';

  private storeShared: StoreSharedInterface = {
    hasPotentiallyRequiredFields: false,
    hasResult: false,
    isReadOnly: false,
    labelTranslationKeyPrefix: '',
    propertyErrors: [],
    readOnlyProperties: [],
  };

  private subForms: { [formIdentifier: string]: Form } = {};

  public constructor(data: StoreData, formFieldMap?: FormFieldMapInterface) {
    this.setData(data);
    this.formFieldMap = formFieldMap || {};
  }

  public clearValidationErrors(): void {
    this.storeShared.hasResult = false;
    this.storeShared.propertyErrors = [];
  }

  public getContext(): string {
    return this.context;
  }

  public getData(): StoreData {
    return this.data
  }

  public getDataOfProperty(property: string): any {
    return this.data[property];
  }

  public getErrorsBySection(sectionName: string): string[] {
    if (!Object.prototype.hasOwnProperty.call(this.formFieldMap, sectionName)) {
      return [];
    }

    let matchingErrors: string[] = [];

    this.formFieldMap[sectionName].forEach((regex: string) => {
      matchingErrors = matchingErrors.concat(this.getErrorsForPropertyRegex(regex));
    });

    return matchingErrors;
  }

  public getErrorsForProperty(propertyName: string): string[] {
    const propertySource: string = this.propertyNamePrefix + propertyName;

    const errors: string[] = [];
    for (const propertyError of this.storeShared.propertyErrors) {
      if (propertyError.source === propertySource) {
        errors.push(propertyError.title);
      }
    }

    return errors;
  }

  public getInvalidFeedback(propertyName: string): string {
    const errors = this.getErrorsForProperty(propertyName);
    return this.getInvalidFeedbackForErrors(errors);
  }

  public getLabelTranslationKeyPrefix(): string {
    return this.storeShared.labelTranslationKeyPrefix;
  }

  public getLocalData(): StoreData {
    return this.localData;
  }

  public getProperty(property: string): any {
    return this.data[property];
  }

  public getSubform(identifier: string): Form {
    if (this.subForms[identifier]) {
      return this.subForms[identifier];
    }

    this.subForms[identifier] = new Form(this.getDataOfSubform(identifier));
    this.subForms[identifier].toSubform(identifier, this);

    return this.subForms[identifier];
  }

  public getSubformForCollection(identifier: string, index: number): Form {
    const data = this.getData();
    if (!data[identifier]) {
      data[identifier] = new CollectionSkeletton();
    }

    if (!Array.isArray(data[identifier].data)) {
      throw 'Given identifier is no collection, use getSubform() instead!';
    }

    if (!data[identifier].data[index]) {
      data[identifier].data[index] = new ItemDataSkeletton();
    }
    const item = data[identifier].data[index];
    const subform = new Form(item.attributes);
    subform.toSubformForCollection(identifier, index, this);
    return subform;
  }

  public getValidationState(propertyName: string): boolean | null {
    const hasErrors = this.hasErrorsForProperty(propertyName);
    return this.getValidationStateForHasErrors(hasErrors);
  }

  public hasChangedData(): boolean {
    return JSON.stringify(this.data) !== this.initialData
  }

  public hasErrorsForProperty(propertyName: string): boolean {
    return this.getErrorsForProperty(propertyName).length > 0;
  }

  public hasErrorsForRegex(regex: RegExp): boolean {
    for (const propertyError of this.storeShared.propertyErrors) {
      if (regex.test(propertyError.source)) {
        return true;
      }
    }
    return false;
  }

  public hasPotentiallyRequiredFields(): boolean {
    return this.storeShared.hasPotentiallyRequiredFields;
  }

  public hasValidationResult(): boolean {
    return this.storeShared.hasResult;
  }

  public isPropertyAllowedToBeEmpty(property: string): boolean {
    return this.allowedEmptyProperties.value.includes(property);
  }

  public isPropertyReadOnly(property: string): boolean {
    if (this.storeShared.isReadOnly) {
      return true;
    }
    return this.storeShared.readOnlyProperties.includes(this.propertyNamePrefix + property);
  }

  public isReadOnly(): boolean {
    return this.storeShared.isReadOnly;
  }

  public setAllowedEmptyProperties(allowedEmptyProperties: string[]): void {
    this.allowedEmptyProperties.value = allowedEmptyProperties;
  }

  public setData(data: StoreData): void {
    this.initialData = JSON.stringify(data);
    this.data = data;
  }

  public setHasPotentiallyRequiredFields(hasPotentiallyRequired: boolean): void {
    this.storeShared.hasPotentiallyRequiredFields = hasPotentiallyRequired;
  }

  public setLabelTranslationKeyPrefix(prefix: string): void {
    this.storeShared.labelTranslationKeyPrefix = prefix;
  }

  public setLocalData(data: StoreData): void {
    this.localData = data;
  }

  public setProperty(property: string, value: any): void {
    this.data[property] = value;
  }

  public setPropertyErrors(propertyErrors: PropertyErrorsInterface[]): void {
    this.storeShared.hasResult = true;
    this.storeShared.propertyErrors = propertyErrors;
  }

  public setReadOnly(readOnly: boolean): void {
    this.storeShared.isReadOnly = readOnly;
  }

  public setReadOnlyProperties(readOnlyProperties: string[]): void {
    this.storeShared.readOnlyProperties = readOnlyProperties;
  }

  /**
   * @return boolean true if the passed error object is a ValidationError, otherwise false
   */
  public setValidationErrors(error: Error | ValidationError): boolean {
    if (!(error instanceof ValidationError)) {
      this.clearValidationErrors();
      return false;
    }

    this.setPropertyErrors(error.getPropertyErrors());
    return true;
  }

  protected getDataOfSubform(identifier: string): any {
    const data = this.getData();
    if (!data[identifier]) {
      data[identifier] = new ItemSkeletton();
    }

    if (Array.isArray(data[identifier].data)) {
      throw 'Given identifier is a collection, use getSubformForCollection() instead!';
    }

    return data[identifier].data.attributes
  }

  protected getErrorsForRegex(regex: RegExp): string[] {
    const allProperties = Object.keys(this.storeShared.propertyErrors);
    const matchedProperties = allProperties.filter(key => regex.test(key));
    let matchedErrors: string[] = [];

    for (const property of matchedProperties) {
      matchedErrors = matchedErrors.concat(this.getErrorsForProperty(property));
    }

    return matchedErrors;
  }

  protected getInvalidFeedbackForErrors(errors: string[]): string {
    return errors.join('\n');
  }

  protected getValidationStateForHasErrors(hasErrors: boolean): boolean | null {
    return hasErrors ? false : null;
  }

  protected toSubform(identifier: string, parent: Form): void {
    this.propertyNamePrefix = parent.propertyNamePrefix + identifier + '/data/attributes/';
    this.context = parent.getContext() + '-' + identifier;
    this.storeShared = parent.storeShared;
  }

  private getErrorsForPropertyRegex(regex: string): string[] {
    return Object.keys(this.storeShared.propertyErrors).filter((key) => {
      return new RegExp('^' + regex + '$', 'g').test(key);
    });
  }

  private toSubformForCollection(identifier: string, index: number, parent: Form): void {
    this.propertyNamePrefix = parent.propertyNamePrefix + identifier + '/data/' + index + '/attributes/';
    this.context = parent.getContext() + '-' + identifier + '-' + index;
    this.storeShared = parent.storeShared;
  }
}
